import { COPY, languageLabel, marketLabel } from './presentationCopy';
import { PresentationFamilyMember, PresentationLanguage, PresentationMarket, PresentationRecord } from './types';

export const LANGUAGE_VERSION_LANGUAGES: PresentationLanguage[] = ['sv', 'fi', 'en'];
export const LANGUAGE_VERSION_MARKETS: PresentationMarket[] = ['SE', 'FI', 'INT'];

export function languageVersionCode(language: PresentationLanguage): string {
  if (language === 'fi') return 'FI';
  if (language === 'en') return 'EN';
  return 'SV';
}

export function familyMembersOf(item: Pick<PresentationRecord, '_id' | 'name' | 'language' | 'market' | 'status' | 'pageFormat' | 'familyMembers'>): PresentationFamilyMember[] {
  if (item.familyMembers?.length) return item.familyMembers;
  return [{
    _id: item._id,
    name: item.name,
    language: item.language,
    market: item.market,
    status: item.status,
    pageFormat: item.pageFormat,
  }];
}

export function isLanguageMarketTaken(
  members: Array<{ language?: string; market?: string }>,
  language: PresentationLanguage,
  market: PresentationMarket,
): boolean {
  return members.some((member) => member.language === language && member.market === market);
}

export function firstOpenLanguageMarket(
  members: Array<{ language?: string; market?: string }>,
  languages: PresentationLanguage[] = LANGUAGE_VERSION_LANGUAGES,
  markets: PresentationMarket[] = LANGUAGE_VERSION_MARKETS,
): {
  language: PresentationLanguage;
  market: PresentationMarket;
} | null {
  const languageList = languages.length ? languages : LANGUAGE_VERSION_LANGUAGES;
  const marketList = markets.length ? markets : LANGUAGE_VERSION_MARKETS;
  for (const language of languageList) {
    for (const market of marketList) {
      if (!isLanguageMarketTaken(members, language, market)) return { language, market };
    }
  }
  return null;
}

export function languageVersionMemberLabel(
  member: Pick<PresentationFamilyMember, 'language' | 'market'>,
  members: Array<Pick<PresentationFamilyMember, 'language'>>,
): string {
  const code = languageVersionCode(member.language);
  const needsMarket = members.filter((item) => item.language === member.language).length > 1;
  return needsMarket ? `${code} · ${marketLabel(member.market)}` : code;
}

export function languageVersionBadge(members: Array<Pick<PresentationFamilyMember, 'language'>>): string {
  const languages = Array.from(new Set(members.map((member) => member.language)));
  if (languages.length <= 1) return COPY.languageVersions;
  return `${languages.length} ${COPY.languageVersionCount}`;
}

export function languageVersionOptionLabel(language: PresentationLanguage, market: PresentationMarket): string {
  return `${languageLabel(language)} · ${marketLabel(market)}`;
}

export interface CatalogCardGroup {
  key: string;
  item: PresentationRecord;
  members: PresentationFamilyMember[];
  openId: string;
  actionId: string;
  displayName: string;
  originReviewPending: boolean;
  libraryUpdatePending: boolean;
}

export function catalogGroupKey(item: Pick<PresentationRecord, '_id' | 'familyId'>): string {
  return String(item.familyId || item._id);
}

export function catalogOpenId(
  item: Pick<PresentationRecord, '_id' | 'familyId'>,
  members: Array<Pick<PresentationFamilyMember, '_id'>>,
): string {
  const familyId = item.familyId ? String(item.familyId) : '';
  if (familyId && members.some((member) => member._id === familyId)) return familyId;
  return item._id;
}

export function catalogActionId(
  item: Pick<PresentationRecord, '_id' | 'familyId'>,
  members: Array<Pick<PresentationFamilyMember, '_id'>>,
): string {
  return catalogOpenId(item, members);
}

export function catalogDisplayName(
  item: Pick<PresentationRecord, 'name' | 'familyId'>,
  members: Array<Pick<PresentationFamilyMember, '_id' | 'name'>>,
): string {
  const familyId = item.familyId ? String(item.familyId) : '';
  const original = familyId ? members.find((member) => member._id === familyId) : undefined;
  return original?.name || item.name;
}

export function catalogSiblingIds(
  members: Array<Pick<PresentationFamilyMember, '_id'>>,
  fallbackId: string,
): string[] {
  const ids = Array.from(new Set(members.map((member) => member._id).filter(Boolean)));
  return ids.length ? ids : [fallbackId];
}

export function catalogFlagLabel(
  member: Pick<PresentationFamilyMember, 'language' | 'market'>,
  members: Array<Pick<PresentationFamilyMember, 'language'>>,
): string {
  const needsMarket = members.filter((item) => item.language === member.language).length > 1;
  const language = languageLabel(member.language);
  return needsMarket ? `${language} · ${marketLabel(member.market)}` : language;
}

export function groupCatalogItems(items: PresentationRecord[]): CatalogCardGroup[] {
  const buckets = new Map<string, PresentationRecord[]>();
  items.forEach((item) => {
    const key = catalogGroupKey(item);
    const rows = buckets.get(key) || [];
    rows.push(item);
    buckets.set(key, rows);
  });

  const groups = Array.from(buckets.entries()).map(([key, rows]: [string, PresentationRecord[]]) => {
    const familyId = rows[0]?.familyId ? String(rows[0].familyId) : '';
    const originalRow = familyId ? rows.find((row) => row._id === familyId) : undefined;
    const item = originalRow || rows[0];
    const membersSource = rows.find((row) => (row.familyMembers?.length || 0) > 1) || item;
    const members = familyMembersOf(membersSource);
    return {
      key,
      item,
      members,
      openId: catalogOpenId(item, members),
      actionId: catalogActionId(item, members),
      displayName: catalogDisplayName(item, members),
      originReviewPending: rows.some((row) => Boolean(row.originReviewPending)),
      libraryUpdatePending: rows.some((row) => Boolean(row.libraryUpdatePending)),
      sortAt: rows.reduce((latest, row) => {
        const next = row.updatedAt || '';
        return next > latest ? next : latest;
      }, ''),
    };
  });

  return groups
    .sort((left, right) => {
      if (left.sortAt === right.sortAt) return 0;
      return left.sortAt < right.sortAt ? 1 : -1;
    })
    .map(({ sortAt: _sortAt, ...group }) => group);
}
